/**
 * Couleur la plus presente d'une region du tableau.
 *
 * Elle sert de FOND a la zone a redessiner, la ou l'on posait un carre blanc.
 * Le blanc n'existe presque jamais dans nos illustrations : il annoncait la
 * zone de loin, obligeait a tout repeindre avant de commencer a se cacher, et
 * faussait l'appreciation des couleurs voisines, qui est pourtant tout le jeu.
 *
 * DEUX EXIGENCES, et la seconde est facile a manquer :
 *
 * 1. FIDELITE. Un aplat legerement decale se voit autant qu'un carre blanc,
 *    juste plus tard. On ne rend donc PAS le centre du seau gagnant, qui peut
 *    tomber a huit niveaux de la vraie teinte, mais la MOYENNE des pixels qui
 *    s'y trouvent : sur un aplat, elle retombe sur la couleur exacte.
 *
 * 2. STABILITE ENTRE NAVIGATEURS. Chaque client recalcule cette couleur de son
 *    cote (elle ne voyage pas dans le protocole), et deux decodeurs WebP
 *    peuvent rendre le meme fichier a un niveau pres. Compter les couleurs
 *    EXACTES ferait alors changer de gagnant entre deux egaux ; les regrouper
 *    par seaux de 8 niveaux absorbe cet ecart, et la moyenne interne ne bouge
 *    que d'une fraction de niveau.
 */
import type { Rect } from './geometry.js';

/** Niveaux par canal apres regroupement : 8 valeurs brutes par seau. */
const LEVELS = 32;
/** Plafond d'echantillons. Une zone de 30 % d'un grand tableau depasse 100 000 pixels. */
const MAX_SAMPLES = 12_000;

function hex(value: number): string {
  return Math.min(255, Math.max(0, Math.round(value))).toString(16).padStart(2, '0');
}

export function dominantColor(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  fallback = '#ffffff',
): string {
  const x = Math.max(0, Math.floor(rect.x));
  const y = Math.max(0, Math.floor(rect.y));
  const width = Math.max(1, Math.floor(rect.width));
  const height = Math.max(1, Math.floor(rect.height));

  let data: Uint8ClampedArray;
  try {
    ({ data } = ctx.getImageData(x, y, width, height));
  } catch {
    // Canvas contamine par une image d'une autre origine : on ne lit rien et
    // le fond reste celui d'avant. Le jeu ne doit pas s'arreter pour un fond.
    return fallback;
  }

  const total = width * height;
  // Pas d'echantillonnage : on saute des pixels plutot que de tout lire, et
  // l'aplat gagnant reste le meme.
  const stride = Math.max(1, Math.ceil(total / MAX_SAMPLES));

  const counts = new Uint32Array(LEVELS * LEVELS * LEVELS);
  const sums = new Float64Array(LEVELS * LEVELS * LEVELS * 3);

  for (let index = 0; index < total; index += stride) {
    const offset = index * 4;
    // Un pixel transparent n'a pas de couleur a offrir.
    if ((data[offset + 3] ?? 0) < 8) continue;
    const r = data[offset] ?? 0;
    const g = data[offset + 1] ?? 0;
    const b = data[offset + 2] ?? 0;
    const bucket =
      ((r >> 3) * LEVELS + (g >> 3)) * LEVELS + (b >> 3);
    counts[bucket] = (counts[bucket] ?? 0) + 1;
    sums[bucket * 3] = (sums[bucket * 3] ?? 0) + r;
    sums[bucket * 3 + 1] = (sums[bucket * 3 + 1] ?? 0) + g;
    sums[bucket * 3 + 2] = (sums[bucket * 3 + 2] ?? 0) + b;
  }

  let best = -1;
  let bestCount = 0;
  for (let bucket = 0; bucket < counts.length; bucket += 1) {
    const count = counts[bucket] ?? 0;
    if (count > bestCount) {
      bestCount = count;
      best = bucket;
    }
  }
  if (best < 0 || bestCount === 0) return fallback;

  const r = (sums[best * 3] ?? 0) / bestCount;
  const g = (sums[best * 3 + 1] ?? 0) / bestCount;
  const b = (sums[best * 3 + 2] ?? 0) / bestCount;
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}
